import React, { useEffect, useState } from "react";
import { api, formatApiError, absUrl } from "../api";
import { Upload, Trash2, Copy, Check, Loader2 } from "lucide-react";

export const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/uploads");
      setItems(data.items || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (ex) { setError(formatApiError(ex, "Upload failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.filename}? Any page still linking to it will show a broken image.`)) return;
    try {
      await api.delete(`/admin/uploads/${it.filename}`);
      setItems((prev) => prev.filter((x) => x.filename !== it.filename));
    } catch (e) { setError(formatApiError(e)); }
  };

  const copy = (path) => {
    navigator.clipboard.writeText(path).then(() => {
      setCopied(path);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <div className="max-w-5xl" data-testid="admin-media">
      <p className="sc-eyebrow">Library</p>
      <h1 className="font-serif text-3xl md:text-4xl text-ink mt-2">Media</h1>
      <p className="sc-body mt-2">Upload images to use anywhere on the site. Max 5&nbsp;MB. JPG, PNG, WebP or GIF.</p>

      <div className="mt-6 flex items-center gap-4">
        <label className="inline-flex items-center gap-2 sc-btn-primary cursor-pointer">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" className="sr-only" onChange={onFile} disabled={uploading} data-testid="media-upload-input" />
        </label>
        {error && <p className="text-magenta text-sm">{error}</p>}
      </div>

      {loading ? (
        <div className="mt-10 text-muteink flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 bg-white border border-blush-200 border-dashed rounded-2xl p-14 text-center">
          <p className="font-serif text-lg text-ink">No images yet.</p>
          <p className="sc-body mt-1">Upload your headshot or any other image to start.</p>
        </div>
      ) : (
        <ul className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="media-list">
          {items.map((it) => (
            <li key={it.filename} className="bg-white border border-blush-200/70 rounded-xl overflow-hidden group">
              <div className="aspect-square bg-blush-50 flex items-center justify-center overflow-hidden">
                <img src={absUrl(it.url)} alt={it.filename} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="text-[11px] text-muteink truncate" title={it.filename}>{it.filename}</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button onClick={() => copy(it.url)} className="inline-flex items-center gap-1 text-[12px] text-ink2 hover:text-magenta" data-testid={`media-copy-${it.filename}`}>
                    {copied === it.url ? <Check size={12} /> : <Copy size={12} />} {copied === it.url ? "Copied" : "Copy path"}
                  </button>
                  <button onClick={() => remove(it)} className="text-muteink hover:text-magenta" title="Delete" data-testid={`media-delete-${it.filename}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminMedia;
